// tutorialContents.js
// This file serves as a central repository for all tutorial content

export const wireMeshTutorial = {
  title: "Wire Mesh Interactive Guidance",
  steps: [
    {
      title: "Step 1: Import into the Wire Bender Interface",
      image: "images/wire_mesh_1.jpg, images/wire_mesh_1.1.png",
      content:
        "Import the 3D model (exported as CSV) into the wire bender interface. The second figure shows the interface of the wire bender. For an overview of how the machine operates, see https://www.pensalabs.com/."
    },
    {
      title: "Step 2: Connect the Wire Joints",
      image: "images/wire_mesh_2.jpg, images/wire_mesh_2.1.jpg, images/kit connection.png",
      content:
        "After bending each wire, use a Fishing Crimping Tool Kit to securely connect the wire joints. The extra figures show the crimping tool in action and the final connected result.",
      videoUrl: "https://www.youtube.com/watch?v=GhfP2qjczJc"
    },
    {
      title: "Step 3: Temporary Tacking for Alignment",
      image: "images/usingtapeorclay.jpg",
      content:
        "Before making permanent joins, lightly tack wires in position with painter’s tape or small dots of modeling clay. This keeps intersections aligned and curves held in place while you check symmetry and proportions."
    },
    {
      title: "Step 4: Stabilize the Structure",
      image: "images/FoundationalWorkflows_Wire mesh sculpture (Algorithm 2).jpg",
      content:
        "Secure the wire frame by connecting different bent segments together, ensuring that the structure is stable and cohesive. You may use additional ties or crimping where needed."
    },
    {
  title: "Tip: Wire Material Properties",
  image: "images/spriness.jpg",
  content:
    "Wires have spring-like properties with flexibility and adjustability characteristics. This means you can slightly bend and fine-tune the shape even after initial forming, allowing for iterative adjustments to achieve the desired geometry."
}
,
    {
      title: "Step 5: Final Adjustments and Inspection",
      image: "",
      content:
        "Inspect the final wire mesh structure carefully, making adjustments to improve balance, strength, and accuracy before covering with fabric or other finishing materials."
    },
    {
      title: "Resources: Wire Bending",
      image: "",
      content:
        "For a comprehensive overview of digital wire bending tools and workflows, see https://www.pensalabs.com/diwire-pro."
    }
  ]
};







export const stackedLayersTutorial = {
  title: "Stacked Laser Cut Layers Interactive Guidance",
  steps: [
    {
      title: "Step 1: Is a Stacked Build the Right Choice?",
      image: "images/Weixin Image_20240905091400.jpg",
      content:
        "Before you commit, check whether stacking makes sense. Stacked builds can get heavy and material-intensive (costly). They also struggle with very thin features and hard-to-reach interior glue joints.",
      tips: [
        "Prefer stacked layers for bulky, smoothly varying forms. Avoid when the model has many needle-thin overhangs or deep internal cavities.",
        "Reduce weight/cost by hollowing the core (omit internal layers, add ribs/spacers) or switching to lighter stock (MDF/foam core for mockups).",
      ],
    },
    {
      title: "Step 2: Export & Prepare Files",
      image: "images/interlocking_1.jpg",
      content:
        "Export your sliced layers as SVG files and confirm each fits your laser bed. Keep filenames ordered (e.g., 01_bottom.svg … 20_top.svg).",
      tips: [
        "Keep units consistent (mm in CAD and laser software).",
        "Account for kerf if you include alignment features—test cut a small coupon first.",
      ],
    },
    {
      title: "Step 3: Laser Cut Each Layer",
      image: "images/stacked_layer_1.jpg",
      content:
        "Load each SVG into the laser software and cut your chosen material (e.g., 3 mm plywood/MDF/acrylic) with appropriate power/speed.",
      tips: [
        "Keep sheets flat; use hold-downs or magnets. Focus correctly before each run.",
        "Masking (tape) reduces smoke marks on plywood; peel before gluing.",
        "Material references: https://makerstock.com/",
      ],
    },
    {
      title: "Step 5: Dry-Fit & Sub-Assemblies",
      content:
        "Sort layers from bottom to top and dry-fit on your registration pins. Build sub-stacks for complex shapes to catch tolerance issues early.",
    },
    {
      title: "Step 6: Gluing Layers Together",
      content:
        "Apply adhesive evenly to contact surfaces and press layers together from the bottom upward. Use cauls/boards to spread pressure.",
      tips: [
        "Wood glue (PVA) for wood/MDF; acrylic cement (solvent-based) for acrylic; CA + activator for quick tacks.",
        "Don’t flood thin areas—excess glue can warp or telegraph through edges.",
        "Clamp evenly or use a flat weight; keep registration pins in during cure for alignment.",
        "Reference video: https://www.youtube.com/watch?v=Il5vFKGv3Uc",
      ],
    },
    {
      title: "Step 7: Drying & Finishing",
      image: "images/Weixin Image_20240905091349.jpg",
      content:
        "Let the adhesive fully cure. Then sand away burn marks/steps and finish as desired.",
      tips: [
        "Start ~120-grit, move to finer grits for edges and contours; sand with the grain on wood.",
        "Wear a dust mask and goggles while sanding.",
        "Optionally fill seams with wood filler before final paint/clear coat.",
      ],
    },
    {
      title: "Troubleshooting: Common Issues",
      content: "If fit, weight, or strength aren’t right, check these first.",
      tips: [
        "Stack is too heavy/costly: hollow the core (skip interior layers), use ribs, or switch to lighter sheets.",
        "Misalignment between layers: add/resize registration pins; leave pins in until final cure.",
        "Visible steps on contours: increase layer count in high-curvature zones or spot-fill and sand.",
        "Edges charred/melty: speed up / reduce power; ensure air assist; use masking on wood.",
        "Glue squeeze-out stains: apply thinner coats; wipe immediately with a damp cloth (PVA) or use minimal solvent cement on acrylic.",
      ],
    },
  ],
};

export const laserCutInterlockingTutorial = {
  title: "Laser Cut Interlocking Interactive Guidance",
  steps: [
    // Append this step to your existing steps array
    {
      title: "Troubleshooting: Common Failures & Quick Fixes",
      image: "images/interlocking_troubleshoot.jpg", // optional
      content:
        "If parts don’t fit or the structure feels weak, run through these quick checks.",
      tips: [
        "Charred/melty edges: reduce power or increase speed; ensure air assist is on. Masking tape can reduce smoke marks on plywood.",
        // Geometry issues
        "Fragile thin struts: increase slice count locally or add small bridges/tabs; keep minimum feature ≥ material thickness.",
        "Lines engrave instead of cut: use vector strokes (hairline), correct color/layer mapping, and no fills.",
        // Assembly sanity
        "Kerf stack-up causes twist: dry-fit in subassemblies first; lightly sand only the interfering slots.",
      ],
    },
    {
      title: "Step 1: Export and Prepare the SVG",
      image: "images/interlocking_1.jpg",
      content:
        "Export the SVG from the workflow and import it into your laser cutter software. Verify units, line colors/weights for cut/engrave, and adjust for kerf.",
      tips: [
        "Refer to the laser cutting basics video: https://www.youtube.com/watch?v=sdACSB8GH3Y",
        "Make sure the material thickness is set correctly in the software (example: 3 mm plywood). Measure with calipers if needed.",
        "Ensure the exported file matches the actual material thickness to avoid loose or overly tight joints.",
      ],
    },
    {
      title: "Step 2: Laser Cut the Parts",
      content:
        "Secure the material flat on the laser bed, focus the lens, and use appropriate power/speed settings for your chosen material.",
      tips: [
        "Ensure the material is flat and firmly fixed to avoid shifting during cutting.",
        "Adjust power and speed settings for different materials (e.g., acrylic, bamboo).",
        "Material references: https://makerstock.com/",
      ],
    },
    {
      title: "Step 3: Test Fit Before Gluing",
      content: "Dry-fit the parts to check tolerances before applying glue.",
      tips: [
        "If joints are too tight, lightly sand or widen slots.",
        "If joints are too loose, use tape or thin shims for a snug fit.",
        "Check if thin elements have enough connection points to maintain the shape.",
      ],
    },
    {
      title: "Step 4: Assemble and Reinforce",
      image: "images/interlocking_2.jpg",
      content:
        "Fit the parts together and apply wood glue at interlock points. Clamp or tape until cured, and wipe away excess glue.",
      tips: [
        "Avoid overapplying glue on thin structures to prevent warping.",
        "For high-stress areas, consider adding small reinforcement tabs or corner blocks.",
        "Maintain even clamping pressure to keep the structure aligned.",
      ],
    },
    {
      title: "Step 5: Finishing Touches",

      content:
        "Once the glue is fully cured, sand off burn marks or rough edges, and optionally apply a protective finish.",
      tips: [
        "Use 120-grit sandpaper for general smoothing; switch to finer grit for delicate areas.",
        "Wear a dust mask and safety goggles when sanding.",
        "Sand along the wood grain for a smoother finish.",
      ],
    },
  ],
};

export const moldMakingTutorial = {
  title: "Mold Making Interactive Guidance",
  steps: [
    {
      title: "Tutorial Link",
      content:
        "Learn more about mold making techniques: https://www.youtube.com/watch?v=MhSH0m3ImNY&t=456s",
      videoUrl: "https://www.youtube.com/watch?v=MhSH0m3ImNY&t=456s"
    },
    {
      title: "Step 0: Printability & Size Check",
      content:
        "Confirm your exported STL halves fit the printer build volume. If your app shows a size warning, scale down or split the model before printing.",
      tips: [
        "Orient halves to minimize supports on sealing faces.",
        "Material: PLA+ for quick tests; PETG for stronger, less brittle molds.",
        "A 0.6 mm nozzle with 0.24–0.3 mm layers speeds printing and strengthens walls."
      ]
    },
    {
      title: "Step 1: 3D Print the Mold Halves",
      content:
        "Slice and print both mold halves. Prioritize stiffness and clean mating faces.",
      tips: [
        "Walls/perimeters: 3–4; infill: 15–30% gyroid or grid.",
        "Enable a brim if first-layer adhesion is marginal.",
        "Avoid sparse top surfaces on the parting plane—use enough top layers to get a sealed surface."
      ]
    },
    {
      title: "Step 2: Post-Process the Halves",
      content:
        "Lightly sand the parting faces flat and deburr the pour/vent edges. Optionally seal porous PLA with a thin CA glue wipe or epoxy to reduce leaks.",
      tips: [
        "Dry-fit the halves and check for light gaps against a window.",
        "If faces rock, spot-sand high points until they close evenly."
      ]
    },
    {
      title: "Step 3: Draft Angle Analysis (In-App)",
      content:
        "In the interface, click “ANALYZE DRAFT ANGLES”. Use the color legend to spot negative draft and undercuts that could lock the part.",
      tips: [
        "If undercuts appear, try moving the parting position slider or redesigning the geometry.",
        "If redesign is not possible, plan a sacrificial demold: break or cut the 3D-printed mold to release the cast part."
      ]
    },
    {
      title: "Step 4: Apply Release Agent",
      image: "images/MM_20240828095808.jpg",
      content:
        "Spray both halves with Mann Ease Release 200 (or similar). Allow solvent to flash off before assembly.",
      tips: [
        "Two light coats > one heavy coat.",
        "Avoid puddling on fine details."
      ]
    },
    {
      title: "Step 5: Assemble and Clamp",
      image: "images/Weixin Image_20240828120749.jpg",
      content:
        "Align the halves and secure with rubber bands, clamps, or screws. Verify the pour and vent paths are clear.",
      tips: [
        "Do a quick water test to check for leaks before mixing silicone.",
        "Add tape around the seam if you expect slight weeping."
      ]
    },
  {
  title: "Step 6: Mix Silicone",
  content:
    "Mix base and curing agent per the manufacturer’s ratio until streak-free. Degas if available, or pour in a thin stream from height to reduce bubbles. See the video for mixing technique.",
  tips: [
    "Scrape the cup walls and bottom while mixing.",
    "If no vacuum chamber, let the mix rest a few minutes before pouring to release bubbles."
  ],
  videoUrl: "https://www.youtube.com/watch?v=nRlnrivhSX0"
}
,
    {
      title: "Step 7: Pour and Cure",
      content:
        "Pour slowly into the mold through the pour hole and allow to cure fully (often 4–24 hours, depending on silicone).",
      tips: [
        "Let silicone climb from the lowest point so air escapes from vents.",
        "Do not demold early—edges tear easily when green."
      ]
    },
    {
      title: "Step 8: Demold Strategies",
      content:
        "Open the mold carefully. If the part is locked by geometry, use the planned sacrificial method: cut or crack the 3D-printed mold to free the part.",
      tips: [
        "Work from the largest draft-friendly face toward tighter features.",
        "If stuck, warm the mold slightly or flex the plastic shell to release adhesion."
      ]
    },
    {
      title: "Troubleshooting",
      content:
        "Common issues and quick fixes.",
      tips: [
        "Leakage at seam: tighten clamps, add tape, or reseal faces with thin CA.",
        "Tears in cast: extend cure time, switch to a higher-shore silicone, or add draft/fillets.",
        "Part won’t release: confirm draft analysis, relocate parting line, or accept a sacrificial mold."
      ]
    }
  ]
};


export const hotWireFoamCuttingTutorial = {
  title: "Hot Wire Foam Cutting Interactive Guidance",
  steps: [
    {
      title: "Intro Example (Instagram)",
      content:
        "A quick reference clip that shows a clean hot-wire foam cutting workflow and finish.",
      videoUrl:
        "https://www.instagram.com/reel/C-HiFSjuj2J/?utm_source=ig_web_copy_link&igsh=Nnkwcnd4aWw5Zzk5",
      tips: [
        "Watch the steady feed and minimal smoke—the wire temperature is well tuned.",
        "Guided edges keep lines straight; replicate this with your laser-cut panels.",
        "Note how offcuts stay supported until final passes to avoid tear-out."
      ]
    },
    {
      title: "Step 1: Safety & Setup",
      content:
        "Before you cut, set up ventilation, PPE, and do a quick temperature/feed calibration.",
      tips: [
        "Work in a well-ventilated area or use fume extraction; wear a respirator (P95/P100) and safety glasses.",
        "Only cut EPS/XPS foam; avoid PVC/unknown plastics (toxic fumes).",
        "Tune wire temp so it cuts smoothly with minimal smoke—adjust voltage rather than forcing the cut.",
        "Keep the wire taut and perpendicular; square your fence/table."
      ]
    },
    {
      title: "Step 2: Prepare the Foam Block",
      image: "images/foamcube.jpg",
      content:
        "Choose a foam block large enough to envelope the model’s outer envelope with margin.",
      tips: [
        "Size the block at least 10–20 mm larger than the model in each axis.",
        "Draw centerlines/arrows on 2–3 faces for alignment with guide panels.",
        "Light spray adhesive can reduce bead shedding on EPS—test first."
      ]
    },
    {
      title: "Step 3: Attach Laser-Cut Guide Panels",
      image: "images/hwf2.JPG",
      content:
        "Tape the laser-cut guide panels (from XY/XZ/YZ projections) to the foam, aligned to your square references.",
      tips: [
        "Dry-fit before taping; toothpicks/bamboo skewers can pin panels temporarily.",
        "Add small tape tabs/bridges so offcuts stay in place until the end.",
        "Account for hot-wire kerf: offset guides outward by ~1–2 mm for tight fits."
      ]
    },
{
  title: "Step 4: Cut with the Hot Wire",
  image: "images/hwf3.JPG, images/foam broken.jpg", // 数组存多个图片
  content:
    "Follow the guide panels with a steady feed. Do rough outer cuts first, then details; keep removable pieces in place as long as possible.",
  videoUrl: "https://www.youtube.com/watch?v=5NbK_H0QmOI",
  tips: [
     "Foam strength warning: Standard EPS/XPS foam has limited load-bearing capacity—heavy parts can break easily. Consider higher-density or structural foams if strength is critical.",
    "If the wire glows bright or smoke increases, you’re too hot/too slow—reduce temp or increase feed.",
    "Keep the wire vertical; for long straights, ride a fence.",
    "Cut order: outer profile → secondary features → finally free any trapped islands.",
   
  ]
},

    {
      title: "Step 5: Refine the Shape",
      content:
        "After roughing, refine with light sanding or quick skim cuts for a smoother finish.",
      tips: [
        "Use 150–220 grit sanding blocks; go gently to preserve crisp edges.",
        "Seal with thinned PVA or lightweight filler if you need a tougher skin (test first).",
        "Vacuum chips instead of blowing—less static and mess."
      ]
    },
    {
      title: "Optional: Test Cut & Calibration",
      content:
        "Do a short test cut on scrap to log voltage/current, feed rate, and kerf so you can reproduce results.",
      tips: [
        "Measure kerf and update your panel/projection offsets accordingly.",
        "If edges look wavy, increase wire tension or slow down slightly."
      ]
    }
  ]
};


// Add more tutorials as needed...

// G-code生成器教程内容
export const gcodeGeneratorTutorial = {
  title: "G-code Generator (Simulator) – Usage Guide",
  steps: [
    {
      title: "Step 0: Is 3D Printing the Right Choice?",
      content:
        "Before using the simulator, sanity-check whether 3D printing fits your goal: (1) Strength & stiffness — does the part need mechanical strength or heat resistance? (2) Size — does it fit your printer’s build volume? (3) Time & cost — are multi-hour prints and filament use acceptable? (4) Finish & tolerances — layered surface and ±0.2–0.5 mm typical tolerances.",
      tips: [
        "For high strength/heat (e.g., near a bulb), consider PETG/ABS/nylon or alternative workflows (laser-cut stacking, molding & casting, CNC).",
        "Designs with many unsupported overhangs/internal cavities may need heavy supports or be a poor fit for FFF.",
      ],
    },
    {
      title: "Common Pitfall: Printing Simple Boxes",
      image: "images/3dprintbroken.jpg",
      content:
        "3D-printing basic box shapes is often slow, filament-heavy, and weak at thin walls. For most enclosures, laser-cut panels + assembly are faster, cheaper, and stronger.",
      tips: [
        "Use 3D printing when geometry can’t be made from flat sheets or requires integrated curved/organic features.",
      ],
    },
    {
      title: "Step 1: What This Simulator Does (and Doesn’t)",
      content:
        "This is a browser-based printing path simulator. It visualizes contours/infill, lets you tweak layer height, infill, and wall count, and shows a rough Estimated Time for learning/comparison.",
      tips: [
        "This workflow does not generate final G-code; it is for simulation only.",
        "Real prints are often longer than the simulator’s ETA due to supports, travel/retraction, and cooling strategies in production slicers.",
      ],
    },
    {
      title: "Step 2: Set Core Parameters",
      content:
        "Adjust layer height, wall count, and infill %. Observe how toolpaths and ETA change.",
      tips: [
        "Draft vs. Quality: 0.28–0.30 mm (faster, rougher) vs. 0.12–0.16 mm (slower, smoother).",
        "Thin features should be ≥ nozzle width; thinner walls may vanish during slicing.",
        "More walls/infill = stronger parts but more time and weight.",
      ],
    },
    {
      title: "Step 3: Read the Preview Warnings",
      content:
        "Use the preview to spot risks a real slicer would warn about: steep overhangs, floating islands, long bridges, ultra-thin struts.",
      tips: [
        "Long unsupported spans will sag: reorient the model or plan supports in a real slicer.",
        "If skinny tips lose perimeters, thicken geometry or increase wall/perimeter count.",
      ],
    },
    {
      title: "Step 4: Export STL, Then Use a Real Slicer",
      content:
        "When satisfied with the simulated strategy, click **Download STL**. In PrusaSlicer/Cura/Bambu Studio choose a printer profile, set material/temperatures, and enable supports if needed. Re-slice and compare the slicer ETA with the simulator’s estimate.",
      tips: [
        "PLA ≈ 200°C nozzle / 60°C bed; PETG ≈ 235°C / 70–80°C (follow manufacturer guidance).",
        "For poor first-layer adhesion, add brim/raft.",
        "Use support blockers/enforcers to control where supports go.",
      ],
    },
    {
      title: "Step 5: Post-Processing",
      content:
        "Let the bed cool before removal. Trim stringing, remove supports, and sand if needed. For functional parts, verify fit; if strength is insufficient, add walls/infill or change material in the next iteration.",
    },

    /* Troubleshooting — each issue is its own step; tips are strings only */
    {
      title: "Troubleshooting: Heat Deformation",
      image: "images/3d-print-melt.png",
      content:
        "Symptom: PLA near heat sources (e.g., lamps) softens or warps. Cause: PLA glass transition ~60°C.",
      tips: [
        "Switch to PETG/ABS/nylon, add ventilation, or increase distance from the heat source.",
        "Material choice affects heat resistance more than shell thickness alone.",
      ],
    },
    {
      title: "Troubleshooting: Surface Roughness",
      image: "images/surface-rough.jpg",
      content:
        "Symptom: Rough, smeared surfaces. Causes: temperature too high, insufficient cooling, or unstable extrusion.",
      tips: [
        "Lower nozzle temperature slightly; improve part cooling; check extrusion calibration and filament path.",
        "Slow down outer walls to improve surface quality.",
      ],
    },
    {
      title: "Troubleshooting: Stringing / Fuzz",
      image: "images/3dprintStringing.jpeg",
      content:
        "Symptom: Stringing or fuzzy surfaces. Common cause: wet filament, inadequate retraction, or temperature too high.",
      tips: [
        "Dry the filament; increase retraction distance/speed; lower nozzle temperature slightly.",
      ],
    },
    {
      title: "Troubleshooting: First Layer & Warping",
      image: "images/FirstLayerWarping.jpg",
      content:
        "Symptom: Poor adhesion or corner lift. Causes: dirty bed, incorrect Z-offset, excessive cooling.",
      tips: [
        "Clean the bed, dial in Z-offset, add a brim, raise bed temperature, and reduce fan for the first layers.",
      ],
    },
    {
      title: "Troubleshooting: Overhangs & Bridges",
      content:
        "Symptom: Sagging on overhangs/bridges. Cause: spans too long or cooling insufficient.",
      tips: [
        "Add supports, reduce speed, increase cooling, or reorient the model to shorten bridge lengths.",
      ],
    },
    {
      title: "Troubleshooting: Weak Parts / Layer Splitting",
      content:
        "Symptom: Layer separation or brittle parts. Cause: poor interlayer bonding or unfavorable load orientation.",
      tips: [
        "Slightly increase nozzle temperature, reduce fan, add perimeters, and reorient so loads run along perimeter direction.",
      ],
    },
    {
      title: "Troubleshooting: ETA Mismatch",
      content:
        "Symptom: Simulator ETA differs from real slicer. Cause: production slicers add supports and more motion/cooling rules.",
      tips: [
        "Expect longer real times; trust the production slicer’s ETA for planning.",
      ],
    },
  ],
};

export const wireCopperElectroplatingTutorial = {
  title: "Wire Copper Electroplating Interactive Guidance",
  steps: [
    {
      title: "Step 1: Import into the Wire Bender Interface",
      image: "images/66.JPG",
      content:
        "Import the 3D model that was previously exported as a CSV file into the wire bender interface."
    },
    {
      title: "Step 2: Connect the Wire Joints",
      image: "images/678.jpg",
      content:
        "Use a Fishing Crimping Tool Kit to securely connect the wire joints after bending each wire.",
      videoUrl: "https://www.youtube.com/watch?v=GhfP2qjczJc"
    },
    {
      title: "Step 3: Temporary Tacking (Tape or Clay)",
      image: "images/usingtapeorclay.jpg",
      content:
        "Lightly tack wires with painter’s tape or small dots of modeling clay to hold curves/intersections as you align and check symmetry.",
      tips: [
        "Remove all tape/clay before plating; non-conductive materials block copper deposition.",
        "Photograph the arrangement so you can restore positions if anything shifts."
      ]
    },
    {
      title: "Step 4: Stabilize the Structure",
      image: "images/FoundationalWorkflows_Wire Mesh Sculpture (Algorithm 1).jpg",
      content:
        "Use taps or mechanical ties to permanently connect bent wires so the assembly is rigid and self-supporting.",
      tips: [
        "Avoid large insulating glue blobs; if adhesives are required, keep them off areas to be plated.",
        "Confirm there are multiple electrical paths across the structure."
      ]
    },
    {
      title: "Step 5: Electrical Continuity & Surface Prep",
      content:
        "Ensure every joint is conductive and the surface is clean.",
      tips: [
        "Use a multimeter to verify low-resistance continuity across distant points.",
        "Degrease with isopropyl alcohol; lightly abrade shiny areas; rinse and dry.",
        "If any parts are non-conductive or coated, apply a conductive paint only where plating is desired."
      ]
    },
    {
      title: "Step 6: Prepare the Copper Anode",
      image: "images/545445.jpg",
      content:
        "Use a 3/4 in. × 2 ft Copper Type L pipe as the anode. Hammer one end flat for a reliable clamp area; this anode connects to the positive terminal.",
      tips: [
        "Expose only the portion meant to be submerged; keep connections dry and above the bath.",
        "Use copper anode for best chemistry balance during plating."
      ]
    },
    {
      title: "Step 7: Cathode Connection & Bath",
      image: "images/54544554545445.jpg",
      content:
        "Connect the negative terminal to the wire structure (the workpiece = cathode). Immerse the structure in a copper sulfate (CuSO₄) solution per manufacturer instructions.",
      tips: [
        "Double-check polarity: copper anode = positive, workpiece = negative.",
        "Hang the part so it does not touch the anode; maintain a consistent gap.",
        "Ensure all areas to be plated are fully submerged and free of trapped bubbles."
      ]
    },
    {
      title: "Step 8: Agitation / Circulation",
      image: "images/54545445545445.jpg",
      content:
        "Turn on the pump or provide gentle agitation to refresh ions at the surface for an even deposit.",
      tips: [
        "Avoid strong jets directly on fragile struts; baffles or indirect flow help.",
        "Keep temperature and agitation consistent for repeatability."
      ]
    },
    {
      title: "Step 9: Power On, Plate, and Monitor",
      image: "images/5454544554545454554543.jpg",
      content:
        "Begin with a low current and ramp gradually while watching the surface. Typical small assemblies start low to prevent burning and dendrites.",
      tips: [
        "Look for smooth, even brightening. Dark/sooty or powdery areas indicate too much current.",
        "Rotate the part occasionally for uniform coverage if your setup allows.",
        "Plan for long runs on slender meshes; thin geometry plates slowly."
      ]
    },
    {
      title: "Step 10: Rinse, Dry, and Finish",
      image: "images/FoundationalWorkflows_Wire copper electroplating.jpg",
      content:
        "Power off, remove the part, rinse in clean water, and dry. Lightly burnish or polish if desired.",
      tips: [
        "Neutralize acid residue if your bath uses acid additives (follow supplier guidance).",
        "If needed, seal with clear coat or wax to slow oxidation."
      ]
    },
    {
      title: "Troubleshooting: Common Issues",
      content:
        "Quick links from symptom → likely cause → fix.",
      tips: [
        "Bare spots: poor cleaning or poor electrical contact → re-clean, recheck continuity, reposition cathode clip.",
        "Burnt/dark powdery copper: current too high → lower current, increase distance to anode, improve agitation.",
        "Uneven thickness: stagnant zones or geometry shadowing → add agitation, reposition part, consider auxiliary anodes.",
        "Peeling/flaking: inadequate surface prep → increase abrasion, degrease thoroughly, start with lower current.",
        "Dendrites/treeing on edges: localized high field → reduce current, round sharp edges, increase agitation."
      ]
    },
    {
      title: "Safety Notes",
      content:
        "Copper sulfate solutions and acid additives require care.",
      tips: [
        "Wear gloves, eye protection, and work with good ventilation.",
        "Keep electrical connections dry and secure; use a current-limited bench supply.",
        "Never mix acids with bleach/ammonia; follow local disposal rules for spent solutions."
      ]
    }
  ]
};


export const paperUnfoldingTutorial = {
  title: "Paper Unfolding — Hands-On Guide",
  steps: [
    {
      title: "Example & Credits",
      image: "images/unfoldstanfordbunny.jpg",
      content:
        "We’ll use this example layout to show the craft steps. This demo is inspired by and credits the repo felixfeliz/paperfoldmodels (we only illustrate the making part here).",
      repoUrl: "https://github.com/felixfeliz/paperfoldmodels",
      tips: [
        "You can print on any paper; card stock (160–220 gsm) holds shape better.",
        "If the template spans multiple pages, tape pages together from the back before cutting."
      ]
    },
    {
      title: "1) Print the Templates",
      content:
        "Open the exported SVG/PDF and print at 100% scale (no “fit to page”). Regular printer is fine.",
      tips: [
        "For large models, use tiling (poster) print and add registration marks if your software supports it.",
        "If ink is light, outline the main parts with a darker pen before cutting."
      ]
    },
    {
      title: "2) Read the Lines",
      content:
        "Black = CUT (solid). Red = MOUNTAIN fold (dashed). Blue = VALLEY fold (dashed). Numbers mark matching edges/tabs.",
      tips: [
        "Our preview shows the same legend: 🔴 red = mountain, 🔵 blue = valley, ⚫ black = cut.",
        "If your printer doesn’t show color clearly, rewrite a tiny “M” (mountain) / “V” (valley) next to dashed lines."
      ]
    },
    {
      title: "3) Cut the Parts",
      content:
        "Cut along the solid black lines to free each part. Leave dashed lines uncut.",
      tips: [
        "Use a sharp hobby knife + metal ruler for long straights; scissors are OK for curves.",
        "Keep offcuts—they can help protect your table when scoring."
      ]
    },
    {
      title: "4) Score & Fold",
      content:
        "Lightly score the dashed lines, then fold: red = mountain (ridge up), blue = valley (ridge down).",
      tips: [
        "Score with a dull blade/empty pen to crease without cutting.",
        "Fold against a ruler edge for crisp lines."
      ]
    },
    {
      title: "5) Join Parts (Tape or Glue)",
      content:
        "Match numbered edges. You can use tape from the back for quick assembly, or glue tabs for a cleaner seam.",
      tips: [
        "For multi-page shapes, align edges first, then bridge the joint on the back with tape.",
        "Dry-fit (no glue) before final joining to check alignment."
      ]
    },
    {
      title: "6) Build Order",
      content:
        "Assemble small sub-parts first, then connect them into the full model. Keep flat areas on the table while closing seams.",
      tips: [
        "Work symmetrically to avoid twist. Add a small internal tape “stitch” to pull edges together if needed.",
        "If a curve fights you, add a second tiny tab or a short tape bridge on the inside."
      ]
    },
    {
      title: "7) Finish",
      content:
        "Check seams and reinforce from the inside with small tape patches if needed. Display or paint as you like.",
      tips: [
        "A light clear coat (spray) can stiffen thin paper—test on a scrap first.",
        "If you want reusability, avoid water-based paints on thin paper."
      ]
    }
  ]
};




// CHANGES - 06/09/2025
// CHANGES - 06/09/2025
export const epoxyCoatingTutorial = {
  title: "Epoxy Coating Interactive Guidance",
  steps: [
    {
      title: "Step 1: Laser Cutting the Layers",
      image: "/images/3.jpg",
      content:
        "Begin by sending the generated SVG files to a laser cutter. Use 3mm polywood as the base material, ensuring the laser cutter is set to the appropriate settings for this material.",
    },
    {
      title: "Step 2: Assembling the Layers",
      image: "/images/20240830104750.jpg",
      content:
        "Start with the bottom layer and work upwards. Apply wood glue to the contact surfaces between layers, aligning them carefully before pressing them together. Hold or clamp the layers in place until the glue sets.",
    },

    {
      title: "Step 3: Sanding the Surface",
      image: "/images/Weixin Image_20240905091349.jpg",
      content:
        "Once the structure is fully assembled and secure, the next step is to smooth the surface using a handheld sander. Begin with a medium-grit sandpaper (around 120 grit) to remove any rough edges or imperfections in the polywood layers. Focus on areas where the layers meet, ensuring that the surface is even and seamless.",
      tips: [
        "Make sure to wear a dust mask and goggles while sanding to protect yourself from wood dust.",
        "Move the sander in circular motions to avoid creating grooves or uneven areas.",
        "If you are sanding a more intricate design, you might want to switch to a finer grit sandpaper or even hand-sand more delicate sections for better control.",
      ],
    },
    {
      title: "Step 4: Applying Resin for Reinforcement",
      image: "/images/FoundationalWorkflows_Epoxy coating.jpg",
      content:
        "After sanding, apply a layer of resin to the structure to both reinforce it and fill any gaps between layers. Use a brush or a spatula to evenly coat the surface with a thin layer of resin. Be sure to work in a well-ventilated area and follow the manufacturer's instructions for mixing and application.",
      videoUrl: "https://www.youtube.com/watch?v=eQZFaWjliKk",
      tips: [
        "Ensure that the resin is applied evenly across all surfaces, paying special attention to the edges and corners to prevent future cracking or peeling.",
        "Use masking tape to cover areas where you don't want resin to drip, especially around delicate sections of the structure.",
        "If you notice bubbles forming in the resin, use a heat gun or hair dryer on low heat to gently eliminate them before the resin cures.",
      ],
    },
    {
      title: "Step 5: Final Sanding and Finishing",
      content:
        "Once the resin has fully cured (refer to the resin manufacturer's curing time, typically 24 hours), perform a final round of sanding. This time, use fine-grit sandpaper (around 220 grit) to smooth out the resin layer and create an even, polished surface.",
      tips: [
        "For the final sanding, be gentle and focus on creating a smooth finish rather than removing a lot of material.",
        "If you're aiming for a glossy finish, you can use a polishing compound after sanding to give the surface a shine.",
        "Optionally, you can apply a protective clear coat after sanding to add durability and a glossy look.",
      ],
    },
  ],
};

export const escapeLoomTutorial = {
  title: "Escape Loom — Weaving on 3D Scaffolds (Quick Guide)",
  steps: [
    {
      title: "Reference & Credits",
      content:
        "Inspired by the paper “EscapeLoom: Fabricating New Affordances for Hand Weaving.” We demonstrate a simplified workflow focused on 3D-printed/laser-cut scaffolds for weaving (no wire bending).",
      paperUrl: "https://dl.acm.org/doi/abs/10.1145/3411764.3445600",
      image: "images/picfrompaper.jpg",
      tips: [
        "Concept attribution to the paper; this is a practical, lightweight adaptation.",
        "Use any rigid scaffold (3D-printed, laser-cut frames, or rods) that exposes anchors for yarn."
      ]
    },
    {
      title: "Step 1: Prepare the 3D Weaving Scaffold",
      content:
        "Load your STL and generate simple paths/contours to position anchors (holes, pegs, slots). Fabricate the scaffold via 3D printing or laser-cut plates assembled into a frame.",
      tips: [
        "Keep anchor spacing regular (e.g., 5–15 mm) for predictable yarn routing.",
        "Round edges or chamfer holes to avoid fiber abrasion.",
        "Test-fit a small corner before fabricating the full-size frame."
      ]
    },
    {
      title: "Step 2: Add Weaving Affordances",
      content:
        "Integrate small pegs, eyelets, or notches where yarn will turn, cross, or tension. Ensure access for hands/tools in tight areas.",
      tips: [
        "Plan pathways: where warp runs, where weft interlaces, and where tension is set.",
        "Removable sub-frames can improve access for deep or enclosed regions."
      ]
    },
    {
      title: "Step 3: Plan Fibers & Paths",
      content:
        "Choose yarns (elasticity, thickness, sheen) and map warp/weft paths across the scaffold. Mark start/end points and tensioning nodes.",
      tips: [
        "High-tension zones: use stronger fibers or more anchors.",
        "Color-code yarns for multi-layer patterns and easier debugging."
      ]
    },
    {
      title: "Step 4: Weave",
      content:
        "Thread warp on anchors first, then interlace weft according to the planned pattern. Maintain even tension and periodically check geometry.",
      tips: [
        "Work from stable anchor lines outward to prevent drift.",
        "If access is hard, weave in sub-assemblies, then mount them onto the main frame."
      ]
    },
    {
      title: "Step 5: Finish",
      content:
        "Lock yarn ends, trim excess, optionally apply a light fabric medium or spray to stabilize. Remove any temporary fixtures.",
      tips: [
        "Hide knots near anchors or under crossings for a clean look.",
        "Document yarn order and tension settings in case of repair or iteration."
      ]
    },
    {
      title: "Result",
      content:
        "Example outcome of the Escape Loom workflow on a 3D scaffold.",
      image: "images/FoundationalWorkflows_Escape Loom.jpg"
    }
  ]
};




export const InterlockingClayWrap = {
  title: "Interlocking Clay Wrap Interactive Guidance",
  steps: [
    {
      title: "Step 1: Laser Cut the SVG File",
      image: "images/2.jpg",
      content:
        "Begin by exporting the SVG file that will be used for laser cutting. Ensure that the material is properly secured on the laser cutter bed, and follow the necessary safety procedures. Once the cut is complete, carefully remove the pieces and prepare them for assembly.",
      tips: [
        "Run a small test cut first to check laser power and speed settings before committing to the full sheet.",
      ],
    },
    {
      title: "Step 2: Assemble with Wood Glue",
      image: null,
      content:
        "Start assembling the interlocking structure by fitting the pieces together. Apply wood glue at each interlock point to ensure a strong bond. Allow sufficient time for the glue to cure before proceeding to the next step.",
      tips: [
        "Wipe away excess glue immediately to avoid uneven surfaces that will affect later clay application.",
      ],
    },
    {
      title: "Step 3: Apply Clay to the Edges",
      image: "images/20240830103050.jpg",
      content:
        "Once the structure is assembled, apply clay along the edges of the interlocking structure. This will help in adhering the fabric later and will also prevent the outer layer of clay from cracking during the next steps.",
      tips: [
        "Keep clay thickness consistent—too thin may crack, too thick may take too long to dry.",
      ],
    },
    {
      title: "Step 4: Wrap the Structure with Fabric",
      image: "images/20240830103331.jpg",
      content:
        "Wrap the entire structure with fabric, ensuring that it adheres well to the clay-covered edges. The fabric will serve as a reinforcement for the structure and help in the next step where more clay is applied.",
      tips: [
        "Use a slightly damp fabric for better adhesion to the clay surface.",
      ],
    },
    {
      title: "Step 5: Apply a Layer of Clay Mixed with Fabric",
      image: "images/FoundationalWorkflows_Laser cut clay sculpture.jpg",
      content:
        "Mix clay with fabric fibers and apply this mixture evenly over the entire structure. Smooth out any gaps and ensure a uniform surface. This layer will strengthen the model and provide a base for the final detailing.",
      tips: [
        "Work in small sections to prevent the mixture from drying before you can smooth it.",
      ],
    },
    {
      title: "Step 6: Add Details or Sculpt for Enhancements",
      content:
        "Once the previous layer has dried, you can add final details to the model. This may involve sculpting finer elements or adding textures to enhance the appearance. Take your time with this step to achieve the desired finish.",
      tips: [
        "Lightly mist the surface with water if the clay starts drying before you finish detailing.",
      ],
    },
  ],
};

export const GlassBlowingMoldMaking = {
  title: "Mold Making for Glass Blowing Interactive Guidance",
  steps: [
    {
      title: "Step 1: Import into the Wire Bender Interface",
      image: "images/FoundationalWorkflows_Wire_Mesh_generating.jpg",
      content:
        "Import the 3D model that was previously exported as a CSV file into the wire bender interface.",
    },
    {
      title: "Step 2: Connect the Wire Joints",
      image: "images/FoundationalWorkflows_Wire_Mesh_generating.jpg",
      content:
        "Use a Fishing Crimping Tool Kit to securely connect the wire joints after bending each wire.",
    },
    {
      title: "Step 3: Stabilize the Structure",
      image: "images/FoundationalWorkflows_Wire_Mesh_generating.jpg",
      content:
        "Use taps to connect different bent wires together, ensuring that the overall structure is stable and cohesive.",
    },
    {
      title: "Step 4: Final Adjustments and Inspection",
      image: "images/FoundationalWorkflows_Wire_Mesh_generating.jpg",
      content:
        "Inspect the final wire mesh structure, making any necessary adjustments to ensure it meets the desired specifications.",
    },
  ],
};

export const NeedleFeltSculptureTutorial = {
  title: "Needle Felt Sculpture Interactive Guidance",
  steps: [
    {
      title: "Step 1: Export 2D Projections (SVG)",
      image: "images/step1.png",
      content:
        "Export clean XY / XZ / YZ projections of your 3D model as SVG. These become registration guides and cutting outlines.",
      tips: [
        "Use a simple line weight and no fills—laser software prefers hairline vectors.",
        "Name layers by view (XY, XZ, YZ) to avoid mix-ups.",
        "If you’ll hot-wire later, include a small kerf offset (≈1–2 mm)."
      ]
    },
    {
      title: "Step 2: Laser Setup",
      image: "images/step2.png",
      content:
        "Import the SVG into your laser software and set parameters for 3 mm plywood (example walk-through in the linked video).",
      videoUrl: "https://www.youtube.com/watch?v=aGlx7OCAykU",
      tips: [
        "Run a tiny test square to confirm clean cut/etch with minimal char.",
        "Use hold-downs or honeycomb magnets to keep sheets flat.",
        "Mirror text labels if you flip sheets during assembly."
      ]
    },
    {
      title: "Step 3: Cut the Guide Panels",
      image: "images/step3.jpg",
      content:
        "Laser-cut the guide panels and label them (Front / Side / Top). Lightly clean edges if needed.",
      tips: [
        "Add tiny witness marks/crosshairs that align across panels.",
        "Keep offcuts—they can help align or support during shaping."
      ]
    },
    {
      title: "Step 4: Prepare the Foam Block",
      image: "images/step4.jpg",
      content:
        "Choose a foam block that fully encloses the model’s width/height/depth with margin.",
      tips: [
        "Leave 10–20 mm extra per side for refinement.",
        "Pre-square at least two faces so panels seat accurately."
      ]
    },
    {
      title: "Step 5: Attach Guide Panels",
      image: "images/step5.jpg",
      content:
        "Tape or pin the guide panels to the foam, aligning axes carefully.",
      tips: [
        "Use painter’s tape tabs so you can peel/reseat without tearing foam.",
        "Toothpicks/skewers make great temporary registration pins."
      ]
    },
    {
      title: "Step 6 (Optional): Rough Shape with Hot Wire",
      image: "images/step6.jpg",
      content:
        "Trace along the panels with a hot-wire cutter to rough the form, keeping offcuts in place until near the end.",
      videoUrl: "https://www.youtube.com/watch?v=5NbK_H0QmOI",
      tips: [
        "Steady feed + minimal smoke = correct temperature.",
        "Cut sequence: outer profile → secondary features → free islands last."
      ]
    },
    {
      title: "Step 7: Needle-Felt the Details",
      image: "images/IMG_6390-1024x768.jpg",
      content:
        "Build surface with wool and barbed needles directly on the foam core. Layer gradually to sculpt contours.",
      videoUrl: "https://www.youtube.com/watch?v=5FfOgujjoBE",
      tips: [
        "Start with coarser needles to lock fibers, then switch finer for finish.",
        "Work from broad volumes to small features; rotate often to check symmetry.",
        "Color-block with different wool shades to ‘preview’ paint."
      ]
    },
    {
      title: "Step 8: Finish",
      image: "images/step8.jpg",
      content:
        "Clean strays, spot-sand foam if needed, and add final felting passes. Remove any remaining guide bits—ready to display or cast.",
      tips: [
        "A light mist of water can tame fuzz before a last pass.",
        "Photograph under raking light to spot uneven planes."
      ]
    }
  ]
};


export const paperMacheTutorial = {
  title: "Paper Mache Shaping Interactive Guidance",
  steps: [
    {
      title: "Step 1: Laser Cut the SVG File",
      image: "images/2.jpg",
      content:
        "Begin by exporting the SVG file that will be used for laser cutting. Ensure that the material is properly secured on the laser cutter bed, and follow the necessary safety procedures. Once the cut is complete, carefully remove the pieces and prepare them for assembly.",
    },
    {
      title: "Step 2: Assemble with Wood Glue",
      image: null,
      content:
        "Start assembling the interlocking structure by fitting the pieces together. Apply wood glue at each interlock point to ensure a strong bond. Allow sufficient time for the glue to cure before proceeding to the next step.",
    },
    {
      title: "Step 3: Apply Masking Tape to the Laser-Cut Structure",
      image: "images/Weixin Image_20240905084725.jpg",
      content:
        "Wrap the entire laser-cut structure with masking tape. This provides a surface that facilitates better adhesion for the paper mache in the following steps. Ensure the tape is applied smoothly without gaps, as this will help create an even surface for the paper mache to adhere to.",
    },
    {
      title: "Step 4: Prepare Paper Pulp Mixture",
      image: "images/微信图片_20240905082619.jpg",
      content:
        "Shred the paper into small, fine pieces and submerge them in warm water. Allow the paper to soak for 24 hours until it breaks down into a pulp. After soaking, blend the paper pulp with white glue in a 1:1 ratio to create the paper mache mixture.",
    },
    {
      title: "Step 5: Apply Paper Mache to the Structure",
      image: "images/Weixin Image_20240905084945.png",
      content:
        "Evenly apply the paper mache mixture to the taped structure. Use a brush to smooth out the paper mache, ensuring uniform coverage across the entire surface. Tip: Applying the mixture in thin layers will help avoid clumps and allow for smoother drying. Ensure all areas are well covered before allowing the structure to dry completely, which may take several hours or overnight, depending on the thickness of the application.",
    },
    {
      title: "Step 6: Dry, Sand, and Finish",
      image: null,
      content:
        "Once the paper mache is fully dry, lightly sand the surface with fine-grit sandpaper to remove rough spots. Optionally, paint the surface with acrylic paint and seal it with a clear protective coat for durability and moisture resistance.",
    },
  ],
  notes: [
    "Safety Tip: Wear gloves when handling glue or paint, and use a mask and eye protection when sanding or spray painting. Work in a well-ventilated area.",
    "Material Alternatives: White glue can be used instead of wood glue; masking tape can be substituted with duct tape or fabric tape, but check for adhesion compatibility with the paper mache.",
  ],
};

export const siliconeMoldTutorial = {
  title: "Silicone Mold Interactive Guidance",
  steps: [
    /* — Put tips first — */
    {
      title: "Food-Safe Tips (Important)",
      tips:[
        "1) Use only food-grade silicone (preferably platinum-cure) and keep tools dedicated for food use.\n" +
        "2) Wash thoroughly before first use and after every use; dry completely.\n" +
        "3) Respect temperature limits: freeze for mousse/ice-cream; verify oven-safe rating before baking.\n" +
        "4) The 3D-printed master is for mold-making only and should not contact food.\n" +
        "5) To avoid undercuts that lock the part, run Draft Angle Analysis in the app before building walls. If extraction is still difficult, add vents/splits—or plan for sacrificial (break-away) demolding."
    ]},

    /* — Food examples moved up — */
    {
      title: "Food Example: Cake Mold (1/3)",
      image: "images/cakeexample1.jpg",
      content:
        "Use food-grade platinum-cure silicone to make a cake/entremet mold. When pouring batter or mousse, lightly tap to degas and level with a scraper."
    },
    {
      title: "Food Example: Cake Mold (2/3)",
      image: "images/cakeexample2.jpg",
      content:
        "Chill or bake depending on the recipe. For mousse/ice-cream cakes, freeze before unmolding for cleaner edges."
    },
    {
      title: "Food Example: Cake Mold (3/3)",
      image: "images/cakeexample3.jpg",
      content:
        "This approach is inspired by community examples. See the reference Reel for plating, layering, and demolding tips.",
      videoUrl: "https://www.instagram.com/reel/DMGPfX2PK1g/"
    },
    {
      title: "Food Example: Chocolate",
      image: "images/Weixin Image_20240905085352.jpg",
      content:
        "Pour tempered chocolate into the silicone mold, tap to release bubbles, and set in a cool place or fridge. Silicone captures fine detail; the smoother your split lines, the crisper the result."
    },

    /* — Core workflow steps — */
    {
      title: "Step 1: Prepare the Manufacturing Base",
      image: "images/20240830111324.jpg",
      content:
        "3D print your master model. Then laser-cut a base plate with two outlines: the model’s XY projection and an outer contour. Use a shallow ‘half-cut’ (don’t cut through) so the lines stay visible and help align the side walls later."
    },
    {
      title: "Step 2: Adhere the Model to the Base",
      image: "images/20240830111426.jpg",
      content:
        "Fix the printed model onto the base with a heat gun/hot-melt glue. Attach clear plastic sheets (or acrylic strips) along the half-cut outer contour to form the mold walls. Keep gaps minimal to prevent silicone leakage."
    },
    {
      title: "Step 3: Mix and Pour Silicone",
      image: "images/20240830111535.jpg",
      content:
        "Mix silicone according to the manufacturer’s ratio and instructions. Pour around the model, tap or gently vibrate to release bubbles, and let it cure fully."
    },

    /* — Resources — */
    {
      title: "How to Mix Silicone",
      content:
        "A quick primer on mixing and handling silicone (ratios, mixing technique, curing):",
      videoUrl: "https://www.youtube.com/watch?v=nRlnrivhSX0"
    }
  ]
};



export const stackMoldTutorial = {
  title: "Stack Mold Interactive Guidance (adapted from StackMold, UIST 2019)",
  steps: [
    {
      title: "Origin & Concept",
      image: "images/oneofpaper'sexample.jpg",
      content:
        "This workflow follows the paper “StackMold: Rapid Prototyping of Functional Multi-Material Objects with Selective Levels of Surface Details” (UIST 2019). Different stacked layers route different colored wax to achieve selective detail levels. Paper: https://dl.acm.org/doi/10.1145/3332165.3347915"
    },
    {
      title: "Step 1: Slice & Export",
      content:
        "Slice the model into planar layers (uniform thickness, e.g., 3 mm). Export SVG outlines for laser cutting."
    },
    {
      title: "Step 2: Assemble Stack Mold",
      content:
        "Laser-cut layers (e.g., wood/MDF/acrylic), clamp to form the outer mold shell."
    },
    {
      title: "Step 3: Multi-Color Wax Casting",
      content:
        "Heat and pour wax for color A into designated layer channels, let it partially set; re-mask/re-stack selected layers, pour color B. Repeat as needed for more colors."
    },
    {
      title: "Step 4: Demold & Finish",
      content:
        "Release the part, trim flash, and lightly reheat to fuse interfaces if necessary."
    }
  ]
};
export const bambooAgentTutorial = {
  title: "Bamboo Agent – 3D-Printed Scaffold for Weaving",
  steps: [
    {
      title: "Step 0: Concept & Reference",
      image: "images/picturefrompaper.jpg",
      content:
        "This workflow generates a 3D-printed scaffold that guides bamboo/rattan weaving so users can quickly reproduce complex woven forms. It is adapted from prior research: Gao et al., “Bamboo Agents: Exploring the Potentiality of Digital Craft by Decoding and Recoding Process,” TEI ’23 (2023). DOI: https://dl.acm.org/doi/10.1145/3569009.3572746",
      tips: [
        "Outcome = printed scaffold + manually woven bamboo/reed over it.",
        "Trade-offs: long print times; thin scaffold members can be fragile during support removal."
      ]
    },
    {
      title: "Step 1: Generate & Export the Scaffold",
      image: "images/examplefrompaper.jpg",
      content:
        "Design or import the target shape, then generate the scaffold (ribs/lattices/anchor loops) and export it as STL for 3D printing. The scaffold provides tie-points and spacing that match the intended weave pattern.",
      tips: [
        "Keep strut thickness ≥ 1.2–1.6 mm (≥ 3 lines with a 0.4–0.5 mm nozzle) for better survivability.",
        "Add small tabs/loops where you plan to anchor or tie the first rows."
      ]
    },
    {
      title: "Step 2: Slice for Thin Scaffolds",
      content:
        "Set slicer parameters to protect small members while keeping print time reasonable.",
      tips: [
        "Walls/perimeters: 2–3; infill: low (0–10%) or gyroid/light to reduce time.",
        "Enable tree or minimal-contact supports; reduce support density; add 2 support interface layers.",
        "Support Z-distance ~0.2–0.25 mm; orient the part to minimize support on the thinnest areas.",
        "Add a brim for bed adhesion on long, skinny footprints."
      ]
    },
    {
      title: "Step 3: Support Removal (Use Pro Tools)",
      content:
        "Scaffold members are slender; remove supports carefully with professional tools to avoid snapping.",
      tips: [
        "Use flush cutters, deburring tools, precision pliers/hemostats, micro saws, and sharp craft knives.",
        "Work from the largest to the smallest contacts; support the strut with your fingers while cutting.",
        "Wear eye protection and gloves; small shards can fly."
      ]
    },
    {
      title: "Step 4: Weave Onto the Scaffold",
      content:
        "Anchor the first row at designated tie-points, then weave in your chosen pattern (plain/twill/hex) while maintaining even spacing and tension.",
      tips: [
        "Use waxed thread/zip-ties/CA at hidden anchors. Trim ties after the structure is stable.",
        "Avoid very sharp bends—let the scaffold define curvature while the weave stabilizes it."
      ]
    },
    {
      title: "Step 5: Finish",
      content:
        "Trim ends, secure loose tails, and consider a light clear coat or resin only if needed for durability. Ensure finishes are compatible with both bamboo and filament.",
      tips: [
        "Spot-sand support scars on the scaffold before weaving, not after.",
        "If the weave must be removable, avoid permanent adhesives on visible faces."
      ]
    },
    {
      title: "Troubleshooting",
      content:
        "Use this checklist when prints break or take too long.",
      tips: [
        "Struts snap during support removal → switch to PETG, increase strut thickness, reduce support contact/interface density, or re-orient to place supports on sturdier zones.",
        "Print time is excessive → 0.6 mm nozzle, thicker layers (0.28–0.3 mm), lower infill, segment the model.",
        "Warping/adhesion issues → add brim, raise bed temp, slow first layer, clean the build plate.",
        "Weave won’t hold shape → add more anchor loops/tabs; tighten spacing; increase scaffold rib count in high-curvature regions."
      ]
    }
  ]
};



export const fingerJointTutorial = {
  title: "Finger Joint Interactive Guidance",
  steps: [
    {
      title: "Step 1: Laser Cut the SVG File",
      image: "images/FoundationalWorkflows_WaffleStructure.jpg",
      content:
        "Begin by exporting the SVG file that will be used for laser cutting. Once the cut is complete, carefully remove the pieces and prepare them for assembly.",
    },
    {
      title: "Step 2: Assemble with Wood Glue",
      image: "images/FoundationalWorkflows_WaffleStructure.jpg",
      content:
        "Start assembling the interlocking structure by fitting the pieces together. Apply wood glue at each interlock point to ensure a strong bond. Allow sufficient time for the glue to cure before proceeding to the next step.",
    },
  ],
};
